import React, { useState, useEffect, useContext, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, Users, TrendingUp, BarChart3, RefreshCw, 
  ChevronRight, User as UserIcon, AlertCircle, Activity
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, LineChart, Line, ReferenceLine, Cell, LabelList
} from 'recharts';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Players = () => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const pendingPid = useRef(searchParams.get('pid'));
  const [activeSport, setActiveSport] = useState('mlb');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('name') || '');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch players based on sport and filters
  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/players/search`, {
        params: {
          sport: activeSport,
          team: selectedTeam !== 'all' ? selectedTeam : undefined,
          query: searchQuery || undefined
        },
        withCredentials: true
      });
      setPlayers(response.data.players || []);
      if (pendingPid.current) {
        const target = (response.data.players || []).find(p => p.id === pendingPid.current);
        if (target) {
          pendingPid.current = null;
          selectPlayer(target);
        }
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/mlb/teams`).then(r => setTeams(r.data.teams || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchPlayers();
    setSelectedPlayer(null);
    setSelectedCategory(null);
  }, [activeSport, selectedTeam]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2 || searchQuery.length === 0) {
        fetchPlayers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Set default category when player is selected
  useEffect(() => {
    if (selectedPlayer?.stat_categories?.length > 0 && !selectedCategory) {
      setSelectedCategory(selectedPlayer.stat_categories[0]);
    }
  }, [selectedPlayer]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPlayers();
    setRefreshing(false);
  };

  const selectPlayer = async (player) => {
    setSelectedPlayer(player);
    setSelectedCategory(player.stat_categories?.[0] || null);
    try {
      const res = await axios.get(`${API}/players/${player.id}/gamelog`, {
        params: { is_pitcher: !!player.is_pitcher, season: 2026, limit: 10 },
        withCredentials: true,
      });
      setSelectedPlayer((prev) =>
        prev && prev.id === player.id
          ? { ...prev, stats: res.data.stats || {} }
          : prev
      );
    } catch (e) {
      console.error('Error fetching game log:', e);
    }
  };

  const getInjuryBadge = (status) => {
    const colors = {
      'Healthy': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Probable': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Questionable': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Day-to-Day': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      'Out': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || colors['Healthy'];
  };

  const getInjuryIcon = (status) => {
    const icons = {
      'Healthy': '🟢',
      'Probable': '🟡',
      'Questionable': '🟠',
      'Day-to-Day': '⚪',
      'Out': '🔴'
    };
    return icons[status] || '🟢';
  };

  const getCurrentStats = () => {
    if (!selectedPlayer || !selectedCategory) return [];
    const categoryKey = selectedCategory.key;
    const stats = selectedPlayer.stats?.[categoryKey] || [];
    return stats.map((s, idx) => ({
      ...s,
      game: s.opponent,
      index: idx
    }));
  };

  const calculateAverage = () => {
    const stats = getCurrentStats();
    if (stats.length === 0) return 0;
    return (stats.reduce((a, b) => a + b.value, 0) / stats.length).toFixed(1);
  };

  const calculateHitRate = () => {
    const stats = getCurrentStats();
    if (stats.length === 0 || !selectedCategory) return '0/0';
    const line = selectedCategory.line;
    const hits = stats.filter(s => s.value > line).length;
    return `${hits}/${stats.length}`;
  };

  const getEVRating = () => {
    const avg = parseFloat(calculateAverage());
    const line = selectedCategory?.line || 0;
    const diff = avg - line;
    const pct = (diff / line) * 100;
    
    if (pct > 10) return { rating: '+EV', color: 'text-green-400', desc: 'Good Value' };
    if (pct < -10) return { rating: '-EV', color: 'text-red-400', desc: 'Avoid' };
    return { rating: 'Neutral', color: 'text-gray-400', desc: 'Fair Line' };
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#0f1e36] border border-[#1e3a5f] rounded p-3">
          <p className="text-muted-foreground text-sm">{label}</p>
          <p className="text-brand-cyan font-bold text-lg">{payload[0].value} {selectedCategory?.label}</p>
          {data.result && (
            <p className={`text-sm ${data.result === 'W' ? 'text-green-400' : 'text-red-400'}`}>
              {data.result === 'W' ? 'Win' : 'Loss'}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Users className="h-8 w-8 text-brand-cyan" />
                Player Analytics
              </h1>
              <p className="text-muted-foreground mt-1">
                Search players, view game logs, and analyze prop trends
              </p>
            </div>
            <Button 
              onClick={handleRefresh}
              variant="outline"
              className="border-[#1e3a5f] text-white hover:bg-[#112240]"
              disabled={refreshing}
              data-testid="refresh-players-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <div className="mb-6 flex items-center gap-2">
            <Badge variant="outline" className="border-red-400 text-red-400">⚾ MLB</Badge>
            <span className="text-sm text-muted-foreground">Advanced metrics, live game logs & prop analysis</span>
          </div>

          {/* Search and Filters */}
          <Card className="bg-[#0f1e36] border-[#1e3a5f] mb-8">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search player name or team..."
                    className="pl-10 bg-[#0a1628] border-[#1e3a5f] text-white placeholder:text-muted-foreground"
                    data-testid="player-search-input"
                  />
                </div>
                
                <Select value={selectedTeam} onValueChange={(v) => { setSearchQuery(''); setSelectedTeam(v); }}>
                  <SelectTrigger className="w-full sm:w-[250px] bg-[#0a1628] border-[#1e3a5f] text-white" data-testid="team-filter-select">
                    <SelectValue placeholder="Filter by team" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1e36] border-[#1e3a5f] max-h-[300px]">
                    <SelectItem value="all" className="text-white hover:bg-[#112240]">All Teams</SelectItem>
                    {teams.map(team => (
                      <SelectItem key={team.abbr} value={team.abbr} className="text-white hover:bg-[#112240]">
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Players List */}
            <div className="lg:col-span-1">
              <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide">
                    Players ({players.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[600px]">
                    {loading ? (
                      <div className="p-4 space-y-4">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div key={i} className="h-20 skeleton rounded-md" />
                        ))}
                      </div>
                    ) : players.length === 0 ? (
                      <div className="p-8 text-center text-muted-foreground">
                        <UserIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No players found</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-[#1e3a5f]">
                        {players.map((player) => (
                          <div 
                            key={player.id}
                            onClick={() => selectPlayer(player)}
                            className={`p-4 cursor-pointer transition-colors hover:bg-[#112240] ${
                              selectedPlayer?.id === player.id ? 'bg-brand-cyan/10 border-l-2 border-brand-cyan' : ''
                            }`}
                            data-testid={`player-item-${player.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <img 
                                src={player.headshot}
                                alt={player.name}
                                className="w-12 h-12 rounded-full bg-[#112240] object-cover"
                                onError={(e) => { e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=200&h=146'; }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-white truncate">{player.name}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-2">
                                  <span className={`sport-icon sport-${activeSport} text-[8px] px-1.5 py-0.5`}>
                                    {player.team}
                                  </span>
                                  <span>{player.position}</span>
                                </div>
                                {/* Injury Status */}
                                <div className="mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full border ${getInjuryBadge(player.injury_status)}`}>
                                    {getInjuryIcon(player.injury_status)} {player.injury_status}
                                  </span>
                                </div>
                              </div>
                              <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Player Stats */}
            <div className="lg:col-span-2">
              {selectedPlayer ? (
                <div className="space-y-6">
                  {/* Player Header */}
                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-6">
                        <img 
                          src={selectedPlayer.headshot}
                          alt={selectedPlayer.name}
                          className="w-24 h-24 rounded-full bg-[#112240] object-cover border-2 border-brand-cyan"
                          onError={(e) => { e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=200&h=146'; }}
                        />
                        <div className="flex-1">
                          <h2 className="font-heading text-3xl font-bold text-white uppercase">
                            {selectedPlayer.name}
                          </h2>
                          <div className="flex items-center gap-4 mt-2 flex-wrap">
                            <span className={`sport-icon sport-${activeSport}`}>{activeSport}</span>
                            <span className="text-brand-cyan font-semibold">{selectedPlayer.team}</span>
                            <span className="text-muted-foreground">{selectedPlayer.position}</span>
                            <span className={`text-sm px-3 py-1 rounded-full border ${getInjuryBadge(selectedPlayer.injury_status)}`}>
                              {getInjuryIcon(selectedPlayer.injury_status)} {selectedPlayer.injury_status}
                            </span>
                          </div>
                          {selectedPlayer.injury_details && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-orange-400">
                              <AlertCircle className="h-4 w-4" />
                              {selectedPlayer.injury_details}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Baseball Savant Advanced Metrics */}
                  {selectedPlayer.savant ? (
                    <Card className="bg-[#0f1e36] border-[#1e3a5f]" data-testid="savant-metrics-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-lg font-bold text-white uppercase tracking-wide flex items-center gap-2">
                          <Activity className="h-5 w-5 text-red-400" />
                          Statcast Metrics
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {(selectedPlayer.savant.type === 'pitcher' ? [
                            { label: 'Fastball Velo', value: selectedPlayer.savant.fastball_velocity, unit: ' mph' },
                            { label: 'Spin Rate', value: selectedPlayer.savant.spin_rate, unit: ' rpm' },
                            { label: 'Whiff %', value: selectedPlayer.savant.whiff_pct, unit: '%' },
                            { label: 'K %', value: selectedPlayer.savant.k_pct, unit: '%' },
                            { label: 'xBA Against', value: selectedPlayer.savant.xba_against, unit: '' },
                            { label: 'xwOBA Against', value: selectedPlayer.savant.xwoba_against, unit: '' },
                            { label: 'Hard-Hit % Allowed', value: selectedPlayer.savant.hard_hit_pct_allowed, unit: '%' },
                            { label: 'Barrel % Allowed', value: selectedPlayer.savant.barrel_pct_allowed, unit: '%' },
                          ] : [
                            { label: 'Avg Exit Velo', value: selectedPlayer.savant.avg_exit_velocity, unit: ' mph' },
                            { label: 'Max Exit Velo', value: selectedPlayer.savant.max_exit_velocity, unit: ' mph' },
                            { label: 'Barrel %', value: selectedPlayer.savant.barrel_pct, unit: '%' },
                            { label: 'Hard-Hit %', value: selectedPlayer.savant.hard_hit_pct, unit: '%' },
                            { label: 'Sweet-Spot %', value: selectedPlayer.savant.sweet_spot_pct, unit: '%' },
                            { label: 'xBA', value: selectedPlayer.savant.xba, unit: '' },
                            { label: 'xSLG', value: selectedPlayer.savant.xslg, unit: '' },
                            { label: 'xwOBA', value: selectedPlayer.savant.xwoba, unit: '' },
                          ]).map((m, i) => (
                            <div key={i} className="bg-[#0a1628] rounded-md p-3 text-center" data-testid={`savant-metric-${i}`}>
                              <div className="text-muted-foreground text-[10px] uppercase tracking-wide">{m.label}</div>
                              <div className="text-xl font-bold text-brand-cyan font-mono mt-1">
                                {m.value != null ? `${m.value}${m.unit}` : '—'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                      <CardContent className="p-4 text-center text-sm text-muted-foreground">
                        No qualified Statcast data for this player (may not meet minimum plate appearances/pitches).
                      </CardContent>
                    </Card>
                  )}

                  {/* Stat Category Selector */}
                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-lg font-bold text-white uppercase tracking-wide">
                        Select Stat Category
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {(selectedPlayer.stat_categories || []).map((cat) => (
                          <Button
                            key={cat.key}
                            variant={selectedCategory?.key === cat.key ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSelectedCategory(cat)}
                            className={selectedCategory?.key === cat.key 
                              ? 'bg-brand-cyan text-[#0a1628] hover:bg-brand-cyan/90' 
                              : 'border-[#1e3a5f] text-white hover:bg-[#112240]'
                            }
                            data-testid={`stat-category-${cat.key}`}
                          >
                            {cat.label}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {selectedCategory && (
                    <>
                      {/* EV Analysis Card */}
                      <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                        <CardContent className="p-4">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div className="bg-[#0a1628] rounded-md p-4 text-center">
                              <div className="text-muted-foreground text-xs uppercase tracking-wide">Line</div>
                              <div className="text-2xl font-bold text-white font-mono mt-1">
                                O/U {selectedCategory.line}
                              </div>
                            </div>
                            <div className="bg-[#0a1628] rounded-md p-4 text-center">
                              <div className="text-muted-foreground text-xs uppercase tracking-wide">L10 Avg</div>
                              <div className="text-2xl font-bold text-brand-cyan font-mono mt-1">
                                {calculateAverage()}
                              </div>
                            </div>
                            <div className="bg-[#0a1628] rounded-md p-4 text-center">
                              <div className="text-muted-foreground text-xs uppercase tracking-wide">Hit Rate</div>
                              <div className="text-2xl font-bold text-brand-lime font-mono mt-1">
                                {calculateHitRate()}
                              </div>
                            </div>
                            <div className="bg-[#0a1628] rounded-md p-4 text-center">
                              <div className="text-muted-foreground text-xs uppercase tracking-wide">EV Rating</div>
                              <div className={`text-2xl font-bold font-mono mt-1 ${getEVRating().color}`}>
                                {getEVRating().rating}
                              </div>
                              <div className={`text-xs ${getEVRating().color}`}>{getEVRating().desc}</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Last 10 Games Chart */}
                      <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                        <CardHeader>
                          <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide flex items-center gap-2">
                            <BarChart3 className="h-5 w-5 text-brand-lime" />
                            Last 10 Games - {selectedCategory.label}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={getCurrentStats()} barCategoryGap="20%" margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                                <XAxis 
                                  dataKey="opponent" 
                                  stroke="#94a3b8" 
                                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                                  interval={0}
                                  angle={0}
                                />
                                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <ReferenceLine 
                                  y={selectedCategory.line} 
                                  stroke="#f59e0b" 
                                  strokeDasharray="5 5" 
                                  strokeWidth={2}
                                  label={{ value: `Line: ${selectedCategory.line}`, fill: '#f59e0b', fontSize: 12, position: 'right' }}
                                />
                                <Bar 
                                  dataKey="value" 
                                  radius={[4, 4, 0, 0]}
                                  animationDuration={500}
                                >
                                  {getCurrentStats().map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={entry.value > selectedCategory.line ? '#8cc63f' : '#00a8e8'} 
                                    />
                                  ))}
                                  <LabelList 
                                    dataKey="value" 
                                    position="top" 
                                    fill="#ffffff"
                                    fontSize={14}
                                    fontWeight="bold"
                                  />
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          
                          {/* Game Results Legend */}
                          <div className="mt-4 flex items-center justify-center gap-6 text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-green-500" />
                              <span className="text-muted-foreground">Win</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-red-500" />
                              <span className="text-muted-foreground">Loss</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-[2px] bg-yellow-500" />
                              <span className="text-muted-foreground">Prop Line ({selectedCategory.line})</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Trend Line Chart */}
                      <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                        <CardHeader>
                          <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-brand-cyan" />
                            Performance Trend
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={getCurrentStats()}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                                <XAxis 
                                  dataKey="opponent" 
                                  stroke="#94a3b8"
                                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                                />
                                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <ReferenceLine 
                                  y={selectedCategory.line} 
                                  stroke="#f59e0b" 
                                  strokeDasharray="5 5"
                                  strokeWidth={2}
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="value" 
                                  stroke="#8cc63f" 
                                  strokeWidth={3}
                                  dot={{ fill: '#8cc63f', strokeWidth: 2, r: 6 }}
                                  activeDot={{ r: 8, fill: '#00a8e8' }}
                                  animationDuration={500}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Game Log Table */}
                      <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                        <CardHeader>
                          <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide flex items-center gap-2">
                            <Activity className="h-5 w-5 text-orange-400" />
                            Game Log
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-muted-foreground text-xs uppercase border-b border-[#1e3a5f]">
                                  <th className="text-left py-3 px-2">Opponent</th>
                                  <th className="text-center py-3 px-2">Result</th>
                                  <th className="text-center py-3 px-2">{selectedCategory.label}</th>
                                  <th className="text-center py-3 px-2">vs Line ({selectedCategory.line})</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getCurrentStats().map((game, idx) => {
                                  const hitLine = game.value > selectedCategory.line;
                                  return (
                                    <tr key={idx} className="border-b border-[#1e3a5f]/50 table-row-hover">
                                      <td className="py-3 px-2 text-white font-medium">{game.opponent}</td>
                                      <td className="py-3 px-2 text-center">
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                                          game.result === 'W' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                        }`}>
                                          {game.result}
                                        </span>
                                      </td>
                                      <td className="py-3 px-2 text-center font-mono font-bold text-white">
                                        {game.value}
                                      </td>
                                      <td className="py-3 px-2 text-center">
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                                          hitLine ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                        }`}>
                                          {hitLine ? 'OVER ✓' : 'UNDER ✗'}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              ) : (
                <Card className="bg-[#0f1e36] border-[#1e3a5f] h-full min-h-[600px]">
                  <CardContent className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <UserIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
                        Select a Player
                      </h3>
                      <p className="text-muted-foreground max-w-md">
                        Search for a player to view their game logs, stat trends, and prop analysis with EV ratings
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Players;
